import { scoreQueryOptions } from "../scores/scoreQueryOptions";
/**
 * The standings screen body, mounted by `apps/app/src/routes/scores.tsx`
 * under `?view=standings`. Refreshes on a 60 second cadence, the same as the
 * mobile screen, and never offers a season control (R8): it always shows
 * whichever season the server currently reports.
 */
import { useStandingsGet } from "@sportsmet/sports-sdk/private";
import { cn, Skeleton } from "@sportsmet/ui";
import { Fragment, type ReactNode, useEffect } from "react";

import { copyForCode, showApiError } from "../../errors/apiErrorBus";
import { SectionEmptyState } from "../../shell/SectionScreen";
import { DetailNavBar } from "../scores/DetailNavBar";
import { StandingsGroupingControl } from "./StandingsGroupingControl";
import { StandingsTable } from "./StandingsTable";
import {
  STANDINGS_BACK_LABEL,
  STANDINGS_ERROR_TITLE,
  STANDINGS_LOADING_LABEL,
  standingsTitle,
} from "./standingsTypes";
import {
  buildStandingsView,
  isStandingsTwoAcross as isTwoAcross,
} from "./standingsViewModel";

export const StandingsScreen = ({
  leagueId,
  grouping,
}: {
  leagueId: string;
  /** The raw `?grouping=` value. `undefined` means "let the server pick". */
  grouping: string | undefined;
}): ReactNode => {
  const query = useStandingsGet(
    { leagueId, grouping: grouping ?? null },
    {
      ...scoreQueryOptions,
      staleTime: 60_000,
      refetchInterval: 120_000,
      refetchIntervalInBackground: false,
    },
  );
  const view = buildStandingsView({
    isPending: query.isPending,
    isError: query.isError && query.data?.data == null,
    envelope:
      query.data?.data != null ? { ...query.data, error: null } : query.data,
  });

  const errorCode = view.kind === "error" ? view.code : null;
  useEffect(() => {
    if (errorCode !== null) {
      showApiError(
        { error: { code: errorCode, message: "" } },
        undefined,
        query.dataUpdatedAt,
      );
    }
  }, [errorCode, query.dataUpdatedAt]);

  return (
    <div className="flex flex-col gap-4">
      {/* The screen's own top row: Back, then "WNBA standings" as the page's
          h1 once the table is in (Scores UX spec v1.0 section 10). */}
      <DetailNavBar
        backLabel={STANDINGS_BACK_LABEL}
        clearKeys={["view", "grouping"]}
        title={
          view.kind === "ready" ? standingsTitle(view.leagueName) : undefined
        }
        testId="standings-nav"
      />
      {view.kind === "loading" ? (
        <Skeleton shape="text" lines={10} label={STANDINGS_LOADING_LABEL} />
      ) : null}
      {view.kind === "error" ? (
        <SectionEmptyState
          headline={STANDINGS_ERROR_TITLE}
          body={copyForCode(view.code)}
        />
      ) : null}
      {view.kind === "empty" ? (
        <SectionEmptyState headline={view.title} body={view.body} />
      ) : null}
      {view.kind === "ready" ? (
        <div className="flex flex-col gap-4">
          <p
            className="-mt-2 text-small text-ink-secondary"
            data-testid="standings-season"
          >
            {view.seasonLabel}
          </p>
          <StandingsGroupingControl
            options={view.groupingOptions}
            selectedKey={view.selectedGroupingKey}
          />
          <div
            className={cn(
              "grid grid-cols-1 gap-6",
              isTwoAcross(view) && "lg:grid-cols-2",
            )}
            data-testid="standings-group-grid"
          >
            {view.groups.map((group) => (
              <Fragment key={group.key}>
                {group.sectionHeader !== null ? (
                  <h2
                    className={cn(
                      "text-h2 text-ink",
                      isTwoAcross(view) && "lg:col-span-2",
                    )}
                  >
                    {group.sectionHeader}
                  </h2>
                ) : null}
                {/* One card per group, its title in bodyStrong. */}
                <section
                  className="flex min-w-0 flex-col gap-2 overflow-hidden rounded-md border border-hairline bg-surface pt-3"
                  data-testid="standings-group-card"
                >
                  <h3 className="px-3 text-body-strong text-ink">
                    {group.title}
                  </h3>
                  <StandingsTable
                    caption={group.title}
                    primaryColumns={view.primaryColumns}
                    secondaryColumns={view.secondaryColumns}
                    rows={group.rows}
                  />
                </section>
              </Fragment>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
